/* global RedactorX */

import React, { useEffect, useRef } from "react";
import { useField } from "react-final-form";
import "../../../../static/assets/redactor/redactorx.min.css";
import "../../../../static/assets/redactor/redactorx.min.js";

interface WYSIWYGEditorProps {
  onChange?: (contents: string) => void;
  onBlur?: (event: FocusEvent, contents: string) => void;
  defaultValue?: string;
  value?: string;
  height?: string;
  rows?: number;
  toolbar?: boolean;
  disable?: boolean;
  name?: string;
  id?: string;
}

export const WYSIWYGEditor: React.FC<WYSIWYGEditorProps> = ({
  onChange,
  onBlur,
  defaultValue,
  value,
  height = null,
  rows = null,
  toolbar = true,
  name,
  id,
}) => {
  if (rows) {
    height = `${rows * 40}px`;
  }
  const editorEl = useRef<HTMLTextAreaElement>(null);
  const redactorRef = useRef<any>(undefined);
  const onChangeRef = useRef<typeof onChange>(onChange);
  const onBlurRef = useRef<typeof onBlur>(onBlur);
  const initialContentRef = useRef<string>(value ?? defaultValue ?? "");

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  const destroyRedactor = (instance: any) => {
    if (!instance) {
      return;
    }

    try {
      if (typeof instance.destroy === "function") {
        instance.destroy();
        return;
      }
      if (instance.app && typeof instance.app.destroy === "function") {
        instance.app.destroy();
        return;
      }
    } catch (error) {
      console.warn("Failed to destroy RedactorX instance:", error);
    }

    // Fallback cleanup in case destroy APIs are unavailable.
    const host = editorEl.current?.parentElement;
    host?.querySelectorAll(".rx-toolbar-container, .rx-container").forEach((node) => node.remove());
    if (editorEl.current) {
      editorEl.current.style.display = "";
    }
  };

  useEffect(() => {
    if (!editorEl.current) {
      return;
    }

    if (redactorRef.current) {
      destroyRedactor(redactorRef.current);
      redactorRef.current = undefined;
    }

    // @ts-ignore: RedactorX is a global variable
    const redactor = RedactorX(editorEl.current, {
      content: initialContentRef.current,
      toolbar: toolbar ? {} : false,
      context: true,
      editor: {
        minHeight: height,
      },
      link: {
        target: "_blank",
      },
      buttons: {
        context: ["link"],
      },
      subscribe: {
        "editor.change": function (e) {
          const callback = onChangeRef.current;
          if (callback) {
            callback(e.params.html);
          }
        },
        "editor.blur": function (e) {
          const callback = onBlurRef.current;
          if (callback) {
            const contents = redactorRef.current?.editor?.getContent?.() ?? "";
            callback(e, contents);
          }
        },
      },
    });
    redactorRef.current = redactor;

    return () => {
      destroyRedactor(redactor);
      if (redactorRef.current === redactor) {
        redactorRef.current = undefined;
      }
    };
  }, [height, toolbar]);

  useEffect(() => {
    const redactor = redactorRef.current;
    if (!redactor?.editor) {
      return;
    }

    const externalValue = value ?? defaultValue ?? "";
    if (redactor.editor.getContent() === externalValue) {
      return;
    }

    try {
      redactor.editor.setContent({ html: externalValue });
    } catch (error) {
      console.warn("Failed to set RedactorX content:", error);
    }
  }, [value, defaultValue]);

  // TODO ref, not sure how to fix this one
  return <textarea ref={editorEl} name={name} id={id}></textarea>;
};

interface WYSIWYGEditorInputProps extends WYSIWYGEditorProps {
  field: string;
}
export const WYSIWYGEditorInput: React.FC<WYSIWYGEditorInputProps> = ({ field, ...props }) => {
  const f = useField(field);
  const value = f.input.value;

  return (
    <WYSIWYGEditor
      {...props}
      defaultValue={value}
      onChange={(contents) => f.input.onChange(contents)}
    />
  );
};
